import {CONFIG} from "../util/config";

export enum Role {
    USER,
    ADMIN,
    SUPER_ADMIN,
    CUSTOM_DASH,
}

export type Company = {
    id: number
    hash: string
    name: string
    isDemo: boolean
}

export type UserDetails = {
    username: string,
    firstName: string,
    lastName: string,
    role: keyof typeof Role | string,
    jobTitle?: string,
    company: Company,
    altEmail: string,
    workPhone: string,
    mobilePhone: string,
    emailPreferences: {
        sharingCohortProjects: boolean,
    }
}

export type Notification = {
    id: number
    startDate: string
    endDate: string
    isActive: boolean
    isReadForUser: boolean
    label: Label
    message: string
}

export enum Label {
    DATA_RELEASE = 'DATA_RELEASE',
    DOWN_TIME = 'DOWN_TIME',
    ENHANCEMENTS = 'ENHANCEMENTS',
    KNOWN_ISSUES = 'KNOWN_ISSUES',
    MISC = 'MISC',
    INSIGHT = 'INSIGHT',
}

export enum TrainingItemType {
    VIDEO = 'VIDEO',
    DOCUMENT = 'DOCUMENT'
}

export type TrainingItem = {
    id: number
    category: string
    type: TrainingItemType
    title: string
    description: string
    url: string
    thumb: string
}


export enum ModulePermission {
    HES_DASHBOARD = "HES_DASHBOARD",
    QUERY_BUILDER = "QB",
    QUERY_BUILDER_V2 = "QUERY_BUILDER_V2",
    COMPANY_QUERY_HISTORY_VIEW = "COMPANY_QUERY_HISTORY_VIEW",
    PRESCRIBING_DASHBOARDS = "PRESCRIBING_DASHBOARDS",
    QOF_DASHBOARD = "QOF_DASHBOARD",
    PROCEDURE_AREA_DASHBOARD = "PROCEDURE_AREA_DASHBOARD",
    COHORT_BUILDER = "COHORT_BUILDER",
    COHORT_HRU = "COHORT_HRU",
    COHORT_PWV = "COHORT_PWV",
    STORYBOARD = "STORYBOARD",
    STORYBOARD_EDIT = "STORYBOARD_EDIT",
    SIMULATIONS = "SIMULATIONS",
    STORYBOARD_RETINA_MARKET_TRENDS = "STORYBOARD_RETINA_MARKET_TRENDS",
    STORYBOARD_VESTRUM_PRACTICE_PERFORMANCE_TRACKER = "STORYBOARD_VESTRUM_PRACTICE_PERFORMANCE_TRACKER"
}

export type Permissions = {
    modulePermissions: ModulePermission[]
    csvExport: boolean
    imageExport: boolean
    pdfExport: boolean
}

export interface Resource {
    code: string
    label: string
    children: Resource[]
}

export type Resources = {
    resources: Resource[]
}

export type GlossaryTerm = {
    id: number,
    title: string,
    description: string,
    datasets: GlossaryDataset[]
}

export type GlossaryDataset = {
    id: number,
    title: string,
}

export const cohortApiUrl = (): string => {
    return `${CONFIG.restPrefix}/cohort`
}

export const userApiUrl = (): string => {
    return `${CONFIG.restPrefix}/user`
}

export const trainingApiUrl = (): string => {
    return `${adminApiUrl()}/training`
}

export const glossaryApiUrl = (): string => {
    return `${adminApiUrl()}/glossary`
}

export const adminApiUrl = (): string => {
    return `${CONFIG.restPrefix}/admin`
}

export const queryBuilderApiUrl = (): string => {
    return `${CONFIG.restPrefix}/query-builder`
}

export const fetchUserDetails = async (): Promise<UserDetails> => {
    const result = await fetch(userApiUrl() + '/user-details', {
        method: 'GET',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
        ...(CONFIG.isDev && { credentials: 'include' })
    })
    return result.json()
}

export const postUserDetails = async (userDetails: UserDetails): Promise<UserDetails> => {
    const result = await fetch(userApiUrl() + '/user-details', {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(userDetails),
        ...(CONFIG.isDev && { credentials: 'include' })
    })
    return handleJsonResponse(result)
}

export const fetchActiveNotifications = () : Promise<Notification[]> => {
    return fetch(adminApiUrl() + '/notification/active', {
        method: 'GET',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
        ...(CONFIG.isDev && { credentials: 'include' })
    })
        .then(result => result.json())
}

export const postMarkNotificationsUnread = async() : Promise<void> => {
    await fetch(adminApiUrl() + '/notification/mark-read', {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
        ...(CONFIG.isDev && { credentials: 'include' })
    })
}

export const fetchTrainingItems = async() : Promise<TrainingItem[]> => {
    const url = trainingApiUrl() + '?includeThumbs=true'
    const result = await fetch(url, {
        ...(CONFIG.isDev && { credentials: 'include' })
    })
    return result.json()
}

export const fetchPermissions = async(): Promise<Permissions> => {
    const url = userApiUrl() + "/permissions"
    const result = await fetch(url, {
        ...(CONFIG.isDev && { credentials: 'include' })
    })
    return result.json()
}

export const fetchResources = async(): Promise<Resources> => {
    const url = userApiUrl() + "/resources"
    const result = await fetch(url, {
        ...(CONFIG.isDev && { credentials: 'include' })
    })
    return result.json()
}

export const fetchGlossary = async(): Promise<GlossaryTerm[]> => {
    const url = glossaryApiUrl()
    const result = await fetch(url, {
        ...(CONFIG.isDev && { credentials: 'include' })
    })
    if (result.status === 200) {
        return result.json()
    }
    throw new Error(await result.json())
}

export const fetchGlossaryDatasets = async(): Promise<GlossaryDataset[]> => {
    const url = `${glossaryApiUrl()}/datasets`
    const result = await fetch(url, {
        ...(CONFIG.isDev && { credentials: 'include' })
    })
    if (result.status === 200) {
        return result.json()
    }
    throw new Error(await result.json())
}
export class JsonApiError extends Error {
    private readonly _errors: object
    constructor(json: object) {
        super(JSON.stringify(json));
        this._errors = json
    }

    get errors(): object {
        return this._errors;
    }
}
export const handleJsonResponse = async (apiResponse: Response) => {
    if (apiResponse.ok) {
        try {
            return await apiResponse.json()
        } catch (e) {
            return undefined
        }
    } else {
        let errJson
        try {
            errJson = await apiResponse.json()
        } catch (e) {
            errJson = {}
        }
        throw new JsonApiError(errJson)

    }
}

export const refreshSession = () => {
    return fetch(`${CONFIG.restPrefix}/session/refresh`, {
        method: 'POST',
    });
}